import React, { useState, useMemo, useCallback, useRef } from "react";
import _ from "lodash";
import classNames from "classnames";

import {
  SearchInput,
  Modal,
  DSProvider,
  IconButton,
  Button,
  EmptyState,
} from "@toddle-edu/ds-web";
import { useI18n, useFetchQuery } from "Hooks";
import { connect } from "react-redux";
import {
  getCurriculumStandardSetsQuery,
  getStandardSetsQuery,
} from "Post/modules/PostQuery";
import {
  DEFAULT_STANDARD_FILTERS,
  getFilteredStandardSetsForTagging,
  getPublishedSubjectStandardQueryVariables,
  getShowNewStandardsUIInV1,
  getStandardsEdges,
} from "Post/utils";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";

import classes from "./StandardSetSelectionModal.scss";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";
import {
  getOrganizationId,
  getSelectedAcademicYear,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
} from "@toddle-edu/ds-icons";
import BreadCrumb from "./BreadCrumb";
import StandardTable from "./StandardTable";
import {
  getCurriculumStandardsFromCache,
  getLearningCourseStandardsFromCache,
} from "Post/modules/PostGraphqlHelpers";
import { getIsSubjectStandard } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils";
import { pypElementMapping } from "Constants/plannerElementMappings";
import { DATA_GRID_COLUMNS } from "AppComponents/StandardsSelectionModal/modules/Constants";
import { getAcademicSetupType } from "Teacher/routes/LearningCourses/modules/Utils";
import {
  COURSE_BASED_GRADING,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_PYP,
} from "Constants/stringConstants";
import { DATA_GRID_COLUMN_TYPES } from "StandardsTable/modules/constants";
import { getOrganizationSubjectsGradesAndTagsQuery } from "AppComponents/StandardsSelectionModal/modules/StandardsSelectionModalQueries";
import {
  NoServiceAsActionInsightsIllustration,
  NoSearchInFilterIllustration,
} from "@toddle-edu/ds-theme";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import { useDispatch } from "Hooks/useDispatch";
import { setToastMsg } from "Login/modules/LoginModule";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";
import {
  getCurriculumIdAndType,
  getSetIdAndSetTypeFromState,
} from "Login/routes/Administrator/commonComponents/SubjectStandardsV2/modules/reduxSelectors";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getDatasetsOrderByArgs } from "Datasets/modules/Utils";

import SkeletonLoader from "UIComponents/SkeletonLoader";

const TEST_ID_PREFIX = "select-learning-elements";

const styles = {
  modalHeaderStyle: { marginBottom: 0 },
  emptyStateSubtitleStyle: {
    width: "75%",
    margin: "auto",
  },
  standardSelectionBody: {
    padding: "0px",
  },
};
/**
 * Renders a single standard set item
 */
const StandardSetItem = ({ title, onClick, testId }) => {
  const handleKeyPress = useCallback(
    event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );
  return (
    <div
      className={classes.standardSetItem}
      onClick={onClick}
      onKeyPress={handleKeyPress}
      data-test-id={`${testId}-${_.lowerCase(title)}`}
      role="button"
      aria-label={`Select standard set: ${title}`}
      tabIndex={0}
    >
      <div className={"text-body"}>{title}</div>
      <ChevronRightOutlined variant="subtle" />
    </div>
  );
};

const StandardSetLoader = React.memo(() => {
  return (
    <div className={classes.standardSetLoader}>
      {_.times(5, index => {
        return <SkeletonLoader width="100%" height="32px" variant="rounded" />;
      })}
    </div>
  );
});

StandardSetLoader.displayName = "StandardSetLoader";

const BLANK_ARRAY = [];
/**
 * Modal component for selecting learning elements/standards
 */
const SelectLearningElementsModal = ({
  isOpen,
  setShowStandardsModal = _.noop,
  showOnlyCourseStandards = true,
  initialSelectedStandards = [],
  testId = TEST_ID_PREFIX,
  showOnlyAdded,
  setShowOnlyAdded = _.noop,
  standardSets = [],
  mode = "create",
  setSearchText,
  searchText,
  learningCourseId,
  curriculumProgramType,
  updatePost,
  elementPYP,
  curriculumProgramId,
  columns,
  organizationId,
  currentCurriculumProgramId,
  isPYPCurriculum,
  showNewStandardsUIInV1 = false,
  currentCurriculumProgramType,
  setId,
  selectedGrades = BLANK_ARRAY,
  courseSubjects = BLANK_ARRAY,
  isDPCurriculum,
}) => {
  const { t } = useI18n(["common", "journal"]);
  const dispatch = useDispatch();
  const id = showOnlyCourseStandards ? learningCourseId : curriculumProgramId;
  const type = showOnlyCourseStandards
    ? "LEARNING_COURSE"
    : "CURRICULUM_PROGRAM";

  const filters = {
    searchText,
    archivalState: "ACTIVE",
  };

  const variables = {
    id,
    type,
    ...(type === "CURRICULUM_PROGRAM"
      ? { plannerElementNodeSetFilters: filters }
      : { plannerElementNodeFilters: filters }),
    isPYPCurriculum,
    ...getDatasetsOrderByArgs(),
  };

  const allStandardsRef = useRef([]);
  const setAllStadardsRef = useCallback(data => {
    allStandardsRef.current = data;
  }, []);

  const parseStandardSetsData = data => {
    let standardSets = [];

    if (showOnlyCourseStandards) {
      standardSets = isPYPCurriculum
        ? _.get(data, "node.benchmarksWrapper.standardsEdge", [])
        : _.get(data, "node.standardsWrapper.standardsEdge", []);
    } else {
      standardSets = _.get(data, "node.plannerElementNodeSets.edges", []);
    }

    return {
      standardSets: getFilteredStandardSetsForTagging(standardSets),
      isData: !_.isEmpty(data.node),
    };
  };

  const passCurriculumStandardSetData = data => {
    const edges = _.map(
      data?.node?.plannerElementNodeSets?.edges || [],
      "node"
    );

    return _.filter(
      _.map(edges, edge => {
        return {
          standardSet: _.omit(edge, "standardSetWrapper"),
          standards: _.map(
            edge?.standardSetWrapper?.publishedStandards?.edges || [],
            "node"
          ),
        };
      }),
      ({ standardSet }) => {
        return !_.get(standardSet, "isArchived", false);
      }
    );
  };

  const skipCondition = (() => {
    if (showNewStandardsUIInV1) {
      return showNewStandardsUIInV1;
    }
    if (showOnlyCourseStandards) {
      return !learningCourseId;
    }
    return !curriculumProgramId;
  })();

  const { data } = useFetchQuery({
    query: getStandardSetsQuery,
    variables,
    skipCondition,
    parseData: parseStandardSetsData,
  });

  const [standardSearchText, setStandardSearchText] = useState("");
  const [selectedStandards, setSelectedStandards] = useState(
    initialSelectedStandards
  );

  const selectedStandardSetsRef = useRef(
    _.find(elementPYP, item =>
      getIsSubjectStandard({ type: pypElementMapping[item?.fieldUID] })
    )?.resolvedMinimalTree?.nodes ||
      _.find(elementPYP, item =>
        getIsSubjectStandard({ type: pypElementMapping[item?.fieldUID] })
      )?.resolvedMinimalTree?.benchmarks ||
      []
  );
  const [standardFilters, setStandardFilters] = useState(() => {
    return { ...DEFAULT_STANDARD_FILTERS };
  });

  const { data: orgsSubjectAndTagsData } = useFetchQuery({
    query: getOrganizationSubjectsGradesAndTagsQuery,
    variables: {
      orgId: organizationId,
      curriculumProgramId: currentCurriculumProgramId,
      genericTagsFilter: {
        types: ["GENERIC"],
        ...getCurriculumIdsFilterForTags({ curriculumProgramId }),
      },
    },
    skipCondition: !organizationId || !currentCurriculumProgramId,
    parseData: data => {
      const subjects = data?.node?.subjects || BLANK_ARRAY;
      const grades = data?.node?.grades || BLANK_ARRAY;
      const tags = data?.node?.genericTagsV2 || BLANK_ARRAY;
      return {
        subjects,
        grades,
        tags,
      };
    },
  });

  const curriculumStandardSetVariables = {
    id: curriculumProgramId,
    type: currentCurriculumProgramType,
    subjectIds: !isPYPCurriculum
      ? isDPCurriculum
        ? []
        : _.map(courseSubjects, "id")
      : _.map(orgsSubjectAndTagsData?.subjects, "id"),
    setId,
    ...getDatasetsOrderByArgs(),
  };

  const {
    data: newStandardsData,
    loading: curriculumStandardsLoading,
    hasData: hasStandardSetData,
  } = useFetchQuery({
    query: getCurriculumStandardSetsQuery,
    variables: curriculumStandardSetVariables,
    skipCondition: !showNewStandardsUIInV1,
    parseData: passCurriculumStandardSetData,
  });

  const filteredStandardSetEdges = useMemo(() => {
    if (showNewStandardsUIInV1) {
      return _.filter(newStandardsData, ({ standardSet }) => {
        return _.chain(standardSet.title)
          .toLower()
          .includes(_.toLower(searchText))
          .value();
      });
    }
  }, [searchText]);

  const standardSetsData = useMemo(() => {
    return showNewStandardsUIInV1
      ? searchText && hasStandardSetData
        ? filteredStandardSetEdges
        : newStandardsData
      : data;
  }, [
    searchText,
    hasStandardSetData,
    filteredStandardSetEdges,
    newStandardsData,
    data,
    showNewStandardsUIInV1,
  ]);

  const [selectedStandardSet, setSelectedStandardSet] = useState(
    _.size(selectedStandards)
      ? showNewStandardsUIInV1
        ? _.first(standardSetsData)?.standardSet?.id
        : _.first(standardSetsData?.standardSets)?.id
      : null
  );

  const onClose = useCallback(() => {
    setShowStandardsModal(false);
  }, [setShowStandardsModal]);

  const getSelectedStandardSet = useMemo(() => {
    const sets = showNewStandardsUIInV1
      ? _.map(standardSetsData, "standardSet")
      : standardSetsData?.standardSets || standardSets;
    return _.find(sets, set => set.id === selectedStandardSet);
  }, [
    selectedStandardSet,
    standardSetsData,
    standardSets,
    showNewStandardsUIInV1,
  ]);

  const onSelect = useCallback(
    ({ standardSet = null, shouldClose = true }) => {
      let node = {};
      const variables = getPublishedSubjectStandardQueryVariables({
        id: learningCourseId,
        standardSetIds: standardSet
          ? _.compact([standardSet])
          : _.compact([selectedStandardSet]),
        curriculumProgramType,
      });
      const curriculumStandardVariable = {
        isPYPCurriculum,
        id: selectedStandardSet,
        filters: {
          subjects: standardFilters.subjects,
          grades: selectedGrades,
          archivalState: "ACTIVE",
          tags: standardFilters.tags,
          searchText: standardSearchText || undefined,
        },
      };

      node = showNewStandardsUIInV1
        ? getCurriculumStandardsFromCache(curriculumStandardVariable)
        : getLearningCourseStandardsFromCache(variables);
      const edges = getStandardsEdges({
        node,
        showCourseStandards: !showNewStandardsUIInV1,
      });

      selectedStandardSetsRef.current = [
        ...(selectedStandardSetsRef.current || BLANK_ARRAY),
        ..._.map(edges, "node"),
      ];

      updatePost({
        elementPYP: [
          ..._.filter(
            elementPYP,
            item =>
              !getIsSubjectStandard({ type: pypElementMapping[item?.fieldUID] })
          ),
          {
            resolvedMinimalTree: {
              [isPYPCurriculum ? "benchmarks" : "nodes"]: _.uniqBy(
                [
                  ..._.map(edges, "node"),
                  ..._.map(allStandardsRef.current || [], "node"),
                  ...(selectedStandardSetsRef.current || BLANK_ARRAY),
                  ...(getShowNewStandardsUIInV1() && !learningCourseId
                    ? [
                        {
                          id: isPYPCurriculum
                            ? _.first(_.map(edges, "node"))?.parent
                            : _.first(_.map(edges, "node"))?.standardsWrapper
                                ?.properties?.subjects[0]?.id,
                          label:
                            getSelectedStandardSet?.label ||
                            getSelectedStandardSet?.title ||
                            "",
                          depth: 0,
                        },
                      ]
                    : []),
                ],
                "id"
              ),
            },
            fieldUID: _.get(STANDARD_UIDS, curriculumProgramType),
            parentType: "ALL",
            value: selectedStandards,
          },
        ],
      });
      shouldClose &&
        dispatch(
          setToastMsg({
            msg: "journal:standards_added_count",
            locale_params: [
              {
                key: "count",
                value: _.size(selectedStandards),
                isPlainText: true,
              },
            ],
            type: "success",
          })
        );
      shouldClose && onClose();
    },
    [
      learningCourseId,
      selectedStandardSet,
      curriculumProgramType,
      elementPYP,
      updatePost,
      selectedStandards,
      dispatch,
      onClose,
      standardSearchText,
      standardFilters,
      selectedGrades,
      isPYPCurriculum,
      showNewStandardsUIInV1,
      getSelectedStandardSet,
    ]
  );

  const handleStandardSetClick = (set, showNewStandardsUIInV1) => () => {
    showNewStandardsUIInV1
      ? setSelectedStandardSet(set.standardSet.id)
      : setSelectedStandardSet(set.id);
  };

  const handleSearchChange = useCallback(
    value => {
      setSearchText(value);
    },
    [setSearchText]
  );

  const handleToggleChange = useCallback(() => {
    setShowOnlyAdded(!showOnlyAdded);
  }, [showOnlyAdded, setShowOnlyAdded]);

  const handleStandardSetSelect = useCallback(
    ({ option, currentSelectedStandardSet, shouldCallOnSelect = true }) => {
      if (shouldCallOnSelect) {
        onSelect({
          standardSet: currentSelectedStandardSet,
          shouldClose: false,
        });
        setSelectedStandardSet(option.key);
      } else {
        setSelectedStandardSet("");
      }
    },
    [onSelect]
  );

  const getStandardSetEmptyStateSubtitle = useCallback(
    ({ isAdmin, t, searchText }) => {
      if (searchText) {
        return t("journal:try_adjusting_your_search");
      }
      if (isAdmin) {
        return t("journal:admin_standard_set_empty_state_subtext");
      }
      return t("journal:teacher_standard_set_empty_state_subtext");
    },
    []
  );

  const resetSearch = useCallback(() => {
    setSearchText("");
  }, [setSearchText]);

  const renderContent = () => {
    const standardSetsToRender = standardSetsData?.standardSets || standardSets;

    if (curriculumStandardsLoading && !hasStandardSetData) {
      return <StandardSetLoader />;
    } else if (
      showNewStandardsUIInV1
        ? !hasStandardSetData
        : !standardSetsToRender.length
    ) {
      return (
        <div className={classes.emptyStateContainer}>
          <EmptyState
            illustration={
              searchText
                ? NoSearchInFilterIllustration
                : NoServiceAsActionInsightsIllustration
            }
            title={
              searchText
                ? t("journal:no_results_for_keyword", { keyword: searchText })
                : t("journal:no_standards_set_added")
            }
            subtitle={getStandardSetEmptyStateSubtitle({
              isAdmin: getIsAdminByRole(),
              t,
              searchText,
            })}
            subtitleStyle={styles.emptyStateSubtitleStyle}
            primaryButton={
              <Button
                onClick={resetSearch}
                type={"outlined"}
                variant={"neutral"}
              >
                {t("common:reset")}
              </Button>
            }
          />
        </div>
      );
    }
    return (
      <div className={classes.standardSetsContainer}>
        {showNewStandardsUIInV1
          ? _.map(standardSetsData, set => {
              const standardSet = set.standardSet;
              return (
                <StandardSetItem
                  key={_.get(standardSet, "id")}
                  title={_.get(standardSet, "title", "")}
                  totalCount={_.size(set.standards)}
                  onClick={handleStandardSetClick(set, showNewStandardsUIInV1)}
                  testId={`${testId}-standard-set`}
                />
              );
            })
          : _.map(standardSetsToRender, set => (
              <StandardSetItem
                key={_.get(set, "id")}
                title={_.get(set, "label", "")}
                totalCount={set.totalCount}
                onClick={handleStandardSetClick(set)}
                testId={`${testId}-standard-set`}
              />
            ))}
      </div>
    );
  };

  const titleClass = classNames({
    [classes.title]: true,
    ["heading-6"]: true,
  });

  const resetStandardSetSelection = standardSet => () => {
    onSelect({
      standardSet,
      shouldClose: false,
    });
    setSelectedStandardSet(null);
  };
  return (
    <DSProvider dsVersion="2.0">
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        testId={testId}
        size="large"
        footerSeparator={true}
        isBodyScrollable={true}
      >
        <Modal.Header
          isClosable={true}
          hasSeparator={!!selectedStandardSet}
          style={styles.modalHeaderStyle}
        >
          <div className={classes.headerContent}>
            <div className={classes.headerRightContent}>
              <div>
                {selectedStandardSet ? (
                  <div className={classes.headerRightContentWithBack}>
                    <div className={classes.backContainer}>
                      <IconButton
                        icon={<ChevronLeftOutlined />}
                        onClick={resetStandardSetSelection(selectedStandardSet)}
                        testId={`${testId}-back`}
                        variant="neutral"
                        type="plain"
                        size="medium"
                      />
                    </div>
                    <div className={classes.titleContainer}>
                      <div className="heading-6" style={{ fontWeight: 700 }}>
                        {_.get(
                          getSelectedStandardSet,
                          "label",
                          _.get(getSelectedStandardSet, "title", "")
                        )}
                      </div>
                      <BreadCrumb
                        standardSets={
                          showNewStandardsUIInV1
                            ? _.map(standardSetsData, "standardSet")
                            : standardSetsData?.standardSets || standardSets
                        }
                        selectedStandardSet={selectedStandardSet}
                        onStandardSetSelect={handleStandardSetSelect}
                        testId={TEST_ID_PREFIX}
                        currentSelectedStandardSet={selectedStandardSet}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={classes.headerRightContent}>
                    <div className={titleClass}>
                      {t("journal:select_standard_set")}
                    </div>
                    {/* Keeping this will be needed for course functionality */}
                    {/* <div className={classes.toggleContainer}>
                      <span
                        className={classNames(
                          "text-body",
                          classes.textSecondary
                        )}
                      >
                        {t("journal:only_show_course_standards")}
                      </span>
                      <ToggleSwitch
                        dsVersion="2.0"
                        size="medium"
                        checked={showOnlyAdded}
                        onChange={handleToggleChange}
                        testId={`${testId}-toggle-course-standards`}
                      />
                    </div> */}
                  </div>
                )}
              </div>
            </div>
            {!selectedStandardSet ? (
              <div className={classes.headerLeftContent}>
                <div className={classes.searchContainer}>
                  <SearchInput
                    value={searchText}
                    onChange={handleSearchChange}
                    placeholder={t("journal:search_for_standard_set")}
                    focusSearchBySlash={true}
                    testId={`${testId}-search`}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </Modal.Header>

        <Modal.Body
          isScrollable={true}
          style={selectedStandardSet ? styles.standardSelectionBody : {}}
        >
          {selectedStandardSet ? (
            <div className={classes.tableContainer}>
              <StandardTable
                selectedStandardSet={selectedStandardSet}
                setStandardFilters={setStandardFilters}
                standardFilters={standardFilters}
                setSelectedStandards={setSelectedStandards}
                selectedStandards={selectedStandards}
                searchText={standardSearchText}
                setSearchText={setStandardSearchText}
                orgsSubjectAndTagsData={orgsSubjectAndTagsData}
                columns={columns}
                showNewStandardsUIInV1={showNewStandardsUIInV1}
                setAllStadardsRef={setAllStadardsRef}
              />
            </div>
          ) : (
            <div>
              {_.size(standardSetsData?.standardSets) ? (
                <div className={classes.tableHeader}>
                  <div className={"text-label"}>
                    {t("journal:standard_sets")}
                  </div>
                </div>
              ) : null}
              <div className={classes.bodyContent}>{renderContent()}</div>
            </div>
          )}
        </Modal.Body>

        {selectedStandardSet ? (
          <Modal.Footer hasSeparator={true}>
            <div className={classes.footerContent}>
              <div className={classes.footerLeftContent}>
                {_.size(selectedStandards) ? (
                  <span
                    className={classNames(
                      "text-label",
                      classes.standardSetCount
                    )}
                  >
                    {t("journal:selected_standard_count", {
                      count: selectedStandards.length,
                    })}
                  </span>
                ) : null}
              </div>
              <div className={classes.footerRightContent}>
                <Button
                  size={"medium"}
                  onClick={onClose}
                  variant="neutral"
                  type={"outlined"}
                >
                  {t("common:cancel")}
                </Button>

                <Button
                  size={"medium"}
                  onClick={onSelect}
                  variant={"progressive"}
                  type={"fill"}
                  disabled={_.isEmpty(selectedStandards)}
                >
                  {mode === "edit" ? t("common:update") : t("common:select")}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        ) : null}
      </Modal>
    </DSProvider>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { id: curriculumProgramId, type: curriculumProgramType } =
    _.get(state, "platform.currentCurriculumProgram", {}) || {};
  const elementPYP = _.get(state, "post.postDetails.elementPYP", []);
  const academicSetupType = getAcademicSetupType();
  const isCourseBasedGrading = academicSetupType === COURSE_BASED_GRADING;

  const columns = isCourseBasedGrading
    ? DATA_GRID_COLUMNS
    : _.without(DATA_GRID_COLUMNS, DATA_GRID_COLUMN_TYPES.SUBJECT_AREAS);

  const { setId } = getSetIdAndSetTypeFromState(state);
  const { currentCurriculumProgramType } = getCurriculumIdAndType(state);
  const selectedGrades =
    state.teacher?.selected_class?.selected_grades || [
      _.get(state, "post.postDetails.course.grade.id", ""),
    ] ||
    BLANK_ARRAY;

  const { courseId } = ownProps;
  const academicYearId = getSelectedAcademicYear(state);
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters: {
      academicYearId: _.get(academicYearId, "id", null),
    },
  });

  const courseSubjects = _.get(courseDetails, "subjects", BLANK_ARRAY);

  const learningCourseId = getSelectedLearningCourseId(state);
  return {
    learningCourseId: learningCourseId
      ? learningCourseId
      : _.get(state, "post.postDetails.course.learningCourse[0].id"),
    currentCurriculumProgramId: getSelectedCurriculumId(state),
    curriculumProgramId,
    curriculumProgramType,
    initialSelectedStandards: _.find(elementPYP, item =>
      getIsSubjectStandard({ type: pypElementMapping[item.fieldUID] })
    )?.value,
    columns,
    organizationId: getOrganizationId(state),
    elementPYP,
    isPYPCurriculum: curriculumProgramType === CURRICULUM_TYPE_PYP,
    isDPCurriculum: curriculumProgramType === CURRICULUM_TYPE_DP,
    currentCurriculumProgramType,
    setId,
    selectedGrades,
    courseSubjects,
  };
};

const StandardSetSelectionModalWrapper = props => {
  const { showOnlyCourseStandards = true } = props;
  const [showOnlyAdded, setShowOnlyAdded] = useState(showOnlyCourseStandards);
  const [searchText, setSearchText] = useState("");

  return (
    <SelectLearningElementsModal
      {...props}
      showOnlyAdded={showOnlyAdded}
      setShowOnlyAdded={setShowOnlyAdded}
      searchText={searchText}
      setSearchText={setSearchText}
      showOnlyCourseStandards={showOnlyCourseStandards}
    />
  );
};

export default connect(mapStateToProps)(StandardSetSelectionModalWrapper);
